import { Col, Menu, MenuProps, Result, Row, Skeleton, Space } from "antd";
import { AlertCircle, SlidersHorizontal, GitBranch, Globe, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import { isNewUI } from "../../v2/flag";
import { EntitySettingsTabs } from "../../v2/components/EntitySettingsTabs";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import { ComponentSchema, IAppDetails } from "../../types";
import { getElasticSearchService, getKubeService } from "../../backend";
import { GetDeploymentHistoryQueryTypeEnum, ServiceDeployment, ServiceDeploymentCommit } from "@microtica/ms-elasticsearch-sdk";
import { GetKubernetesStatusResponseTypeEnum } from "@microtica/ms-kube-sdk";
import { latestDeployment, runningVersion } from "../../v2/data/runningVersion";

const AppSettingsRoot = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { projectId, clusterId, namespace, appName } = useParams() as {
        projectId: string;
        envId: string;
        componentId: string;
        clusterId: string;
        namespace: string;
        appName: string;
    };
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("general");
    const [app, setApp] = useState<IAppDetails>();
    const [menuItems, setMenuItems] = useState<MenuProps["items"]>();
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        setActiveTab(location.pathname.split("/")[12] || "environment");
    }, [location.pathname]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [
                    { data: deploymentStatus },
                    { data: cluster },
                    { data: { response: allDeployments } }
                ] = await Promise.all([
                    getKubeService().getMicroserviceDeploymentStatus(
                        appName,
                        clusterId,
                        namespace,
                        projectId
                    ),
                    // Settings only require stored cluster metadata. The live status
                    // endpoint is expected to fail while an environment is undeployed.
                    getKubeService().getKubernetes(clusterId, projectId),
                    getElasticSearchService().getDeploymentHistory(
                        projectId,
                        {
                            type: GetDeploymentHistoryQueryTypeEnum.Service,
                            serviceNames: [appName],
                            kubernetesIds: [clusterId],
                            // Enough to find this namespace's newest deploy among the record's other namespaces
                            limit: 20,
                            offset: 0
                        }
                    )
                ]);

                const lastDeployment = latestDeployment(allDeployments as ServiceDeployment[], namespace);
                const pods = deploymentStatus.pods || [];
                const clusterType = cluster.type as unknown as GetKubernetesStatusResponseTypeEnum;
                const associatedNamespace = cluster.associatedNamespace;
                const domain = deploymentStatus.configurations.find(c => c.key === "MIC_DOMAIN_NAME")?.value || "n/a";
                const lastDeploymentKubeConfig = JSON.parse(lastDeployment?.kubeConfig || "[{}]");
                const getNumericConfiguration = (key: string) =>
                    parseInt(deploymentStatus.configurations.find(c => c.key === key)?.value || "0");

                const appDetails = {
                    name: appName,
                    description: lastDeployment?.description,
                    schema: JSON.parse(lastDeployment?.schemaJSON || "{}") as ComponentSchema,
                    image: runningVersion(appName, pods, lastDeployment),
                    namespace,
                    configuration: lastDeployment?.configurations || deploymentStatus.configurations,
                    pipelineId: (lastDeployment?.commit as ServiceDeploymentCommit & { pipelineId: string })?.pipelineId,
                    clusterType,
                    associatedNamespace,
                    domain,
                    isDomainWorking: clusterType === GetKubernetesStatusResponseTypeEnum.Shared || !!associatedNamespace ? true : checkIfDomainIsWorking(lastDeploymentKubeConfig),
                    lastDeploymentIncludesIngressSetup: !!lastDeploymentKubeConfig.find((r: any) => r.kind === "Ingress" && r.spec?.ingressClassName === "nginx"),
                    monitoring: {
                        initialCpu: getNumericConfiguration("MIC_AS_CPU"),
                        cpuLimit: getNumericConfiguration("MIC_AS_MAX_CPU"),
                        cpuUsage: deploymentStatus.usage?.averageCpu!,
                        initialMemory: getNumericConfiguration("MIC_AS_MEMORY"),
                        memoryLimit: getNumericConfiguration("MIC_AS_MAX_MEMORY"),
                        memoryUsage: deploymentStatus.usage?.averageMemory!,
                        minReplicas: getNumericConfiguration("MIC_AS_MIN_REPLICAS"),
                        maxReplicas: getNumericConfiguration("MIC_AS_MAX_REPLICAS"),
                        runningPods: pods.filter(pod => pod.status.phase === "Running").length,
                        failedPods: pods.filter(pod => ["Failed", "Unknown"].includes(pod.status.phase)).length
                    }
                };

            const items: MenuProps["items"] = [];

            items.push({
                key: "environment",
                label: <Space>
                    <SlidersHorizontal size={16} style={{ display: 'flex', alignItems: 'center' }} />
                    Environment Variables
                </Space>
            });

            if (appDetails.pipelineId) {
                items.push({
                    key: "pipeline",
                    label: <Space>
                        <GitBranch size={16} style={{ display: 'flex', alignItems: 'center' }} />
                        Pipeline
                    </Space>
                });
            }

            items.push({
                key: "scaling",
                label: <Space>
                    <TrendingUp size={16} style={{ display: 'flex', alignItems: 'center' }} />
                    Scaling
                </Space>
            });

            items.push({
                key: "domain",
                label: <Space>
                    <Globe size={16} style={{ display: 'flex', alignItems: 'center' }} />
                    Domain
                </Space>,
                disabled: appDetails.clusterType === "shared" || !!appDetails.associatedNamespace,
                title: appDetails.clusterType === "shared" || !!appDetails.associatedNamespace ?
                    `Domain configuration is not enabled for apps deployed on a ${appDetails.clusterType === "shared" ? "shared cluster" : "cluster with access limitation"}` :
                    undefined
            });

            items.push({
                key: "danger-zone",
                label: <Space>
                    <AlertCircle size={16} style={{ display: 'flex', alignItems: 'center' }} />
                    Danger Zone
                </Space>,
                danger: true
            });

                setMenuItems(items);
                setApp(appDetails as any);
                setLoadError(false);
            } catch (error) {
                setLoadError(true);
            } finally {
                setLoading(false);
            }
        }

        loadData();
    }, [appName, clusterId, namespace, projectId]);


    const checkIfDomainIsWorking = (kubeConfig: any[]) => {
        // ingress-tls is used as a default secretName for shared clusters; 
        // ingress-tls is part of the certificate resource in a personal cluster => domain is not yet configured
        const hasCertificate = !!kubeConfig.find(r => r.kind === "Certificate" && r.apiVersion === "cert-manager.io/v1" && r.spec?.issuerRef?.kind === "ClusterIssuer" && r.spec?.secretName !== "ingress-tls");
        return hasCertificate;
    };

    // The new UI shows the tabs as pills above the tab, like project settings
    if (!loadError && isNewUI()) {
        return loading ? <Skeleton /> : (
            <EntitySettingsTabs items={menuItems} active={activeTab} onSelect={key => { navigate(key); setActiveTab(key); }}>
                <Outlet context={{ app }} />
            </EntitySettingsTabs>
        );
    }

    return (
        loadError ?
            <Result
                status="error"
                title="Unable to load application settings"
                subTitle="The application metadata could not be loaded. Please try again."
            /> :
        <Row gutter={24}>
            <Col span={6}>
                {
                    loading ? <Skeleton /> :
                        <Menu
                            mode="inline"
                            className="ant-card-bordered"
                            selectedKeys={[activeTab]}
                            onSelect={(item) => {
                                navigate(item.key);
                                setActiveTab(item.key);
                            }}
                            items={menuItems}
                        />

                }
            </Col>
            <Col span={18}>
                {
                    loading ? <Skeleton /> :
                        <Outlet context={{ app }} />
                }
            </Col>
        </Row >
    );

}

export default AppSettingsRoot;
